import { useEffect } from "react";

/**
 * Makes every reactstrap Modal draggable by its header, app-wide.
 *
 * Uses a single delegated `mousedown` listener so no per-modal wiring is
 * needed: grabbing a `.modal-header` and moving the mouse translates the
 * associated `.modal-content`. Each modal keeps its position while open and
 * resets naturally when it remounts (fresh DOM node => no stored offset).
 */
const offsetByContent = new WeakMap();

const getOffset = (content) =>
  offsetByContent.get(content) || { x: 0, y: 0 };

const applyTransform = (content, offset) => {
  content.style.transform = `translate(${offset.x}px, ${offset.y}px)`;
};

const STYLE_ELEMENT_ID = "draggable-modal-styles";

const ensureCursorStyles = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = ".modal-header { cursor: move; }";
  document.head.appendChild(style);
};

export const useDraggableModals = () => {
  useEffect(() => {
    ensureCursorStyles();
    const drag = { active: false, content: null, startX: 0, startY: 0, offset: null };

    const handleMouseMove = (event) => {
      if (!drag.active || !drag.content) return;
      drag.offset.x += event.clientX - drag.startX;
      drag.offset.y += event.clientY - drag.startY;
      drag.startX = event.clientX;
      drag.startY = event.clientY;
      applyTransform(drag.content, drag.offset);
    };

    const handleMouseUp = () => {
      if (!drag.active) return;
      drag.active = false;
      drag.content = null;
      drag.offset = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    const handleMouseDown = (event) => {
      // Only left button drags.
      if (event.button !== 0) return;
      const header = event.target.closest(".modal-header");
      if (!header) return;
      // Don't hijack the close button or any interactive control in the header.
      if (event.target.closest("button, a, input, select, textarea, [role='button']")) {
        return;
      }
      const content = header.closest(".modal-content");
      if (!content) return;

      drag.active = true;
      drag.content = content;
      drag.startX = event.clientX;
      drag.startY = event.clientY;
      drag.offset = getOffset(content);
      offsetByContent.set(content, drag.offset);

      header.style.cursor = "move";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      event.preventDefault();
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);
};

export default useDraggableModals;
